import { Observable } from 'rxjs/Rx';
import { Injectable } from '@angular/core';

import { ResponseService } from './response.service';
import { TestTakerService } from './test-taker.service';
import * as Enum from '../dtos/enums';
import { ConfigHelper } from './config.helper';

@Injectable()
export class PreviewService {

    constructor(
        private testTakerService: TestTakerService
    ) {
    }

    getPreviewResponsesHtml(interactions: PgRendererDto.InteractionDefinition[], responseService: ResponseService, responseScores: PgRendererDto.ResponseScoresDto): string {
        let html = "";
        interactions.forEach(interaction => {
            const response = responseService.getResponse(interaction);
            const responseScore = responseScores.ResponseScores.find(r => r.InteractionId == interaction.InteractionId);
            html += this.createRowHtml(interaction, response, responseScore);
        });
        return `
        <table class="table table-bordered preview-response">
            <tr>
                <th class="text-center">Ref #</th>
                <th class="text-center">Score(s)</th>
                <th>Your Response(s)</th>
                <th>Answer(s)</th>
            </tr>
            ${html}
        </table>
        `;
    }

    private createRowHtml(interaction: PgRendererDto.InteractionDefinition, response: PgRendererDto.InteractionResponse, responseScore: PgRendererDto.ResponseScore): string {
        let userResponseHtml = "";
        if (response) {
            userResponseHtml = this.createUserResponseHtml(interaction, response);
        }
        const correctAnswerHtml = this.createCorrectAnswerHtml(interaction);
        return `
        <tr>
            <td class="text-center">${interaction.InteractionRefNum}</td>
            <td class="text-center">${(responseScore && responseScore.Score != null) ? responseScore.Score : ''}</td>
            <td>${userResponseHtml}</td>
            <td>${correctAnswerHtml}</td>
        </tr>
        `;
    }

    public createUserResponseHtml(interaction: PgRendererDto.InteractionDefinition, response: PgRendererDto.InteractionResponse): string {
        let html = "";
        if (!response) {
            return html;
        }

        switch (interaction.InteractionTypeId as any) {
            case Enum.InteractionTypeEnum.Speaking: {
                if (ConfigHelper.isIppMode()) {
                    const sourcePath = ConfigHelper.testContentServerApiUrl() + "/api/resource/audio/" + this.testTakerService.testTakerInfo.TestTakerId + "/" + this.testTakerService.testTakerInfo.TestSessionId + "/" + response.InteractionId;
                    html = "<audio src='" + sourcePath + "' controls=''></audio>";
                }
                break;
            }
            case Enum.InteractionTypeEnum.Choice: {
                interaction.ChoiceInteraction.SimpleChoices.forEach(choice => {
                    const item = response.Items.find(x => x.Value == choice.Identifier);
                    if (item) {
                        html += `${choice.Html}<br>`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.InlineChoice: {
                interaction.InlineChoiceInteraction.InlineChoices.forEach(choice => {
                    const item = response.Items.find(x => x.Value == choice.Identifier);
                    if (item) {
                        html += `${choice.Html}<br>`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.Hotspot: {
                response.Items.forEach(item => {
                    if (item.Value) {
                        html += `${item.Value}<br>`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.Order: {
                response.Items.forEach(item => {
                    const choice = interaction.OrderInteraction.SimpleChoices.find(c => c.Identifier == item.Value);
                    if (choice) {
                        html += `${choice.Html}<br>`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.ExtendedText: {
                response.Items.forEach(item => {
                    if (item.Text) {
                        html += `${item.Text}`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.TextEntry: {
                response.Items.forEach(item => {
                    if (item.Value) {
                        html += `${item.Value}`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.Associate: {
                let tempHtml: string;
                interaction.AssociateInteraction.ResponseDeclaration.Mapping.MapEntries.forEach((entry, entryIndex) => {
                    tempHtml = "";
                    const pairedItems = response.Items.filter(x => x.Key == entryIndex.toString());
                    pairedItems.forEach((item, itemIndex) => {
                        const choice = interaction.AssociateInteraction.SimpleAssociableChoices.find(x => x.Identifier == item.Value);
                        if (choice) {
                            tempHtml += `${choice.Html}`;
                            if (itemIndex < pairedItems.length - 1) {
                                tempHtml += " : ";
                            }
                        }
                    })
                    if (tempHtml.length > 0) {
                        html += tempHtml + "<br>";
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.Match: {
                const matchInteraction = interaction.MatchInteraction;
                const groups = matchInteraction.SimpleMatchSets[1].SimpleAssociableChoices;
                const choices = matchInteraction.SimpleMatchSets[0].SimpleAssociableChoices;

                groups.forEach(group => {
                    html += `<strong>${group.Html}</strong><br>`;
                    const groupItems = response.Items.filter(x => x.Key == group.Identifier);
                    groupItems.forEach(groupResponse => {
                        const choice = choices.find(c => groupResponse.Value == c.Identifier);
                        if (choice) {
                            html += `- ${choice.Html} <br>`;
                        }
                    });
                    html += "<br>";
                });
                break;
            }
            default:
                break;
        }
        return html;
    }

    public createCorrectAnswerHtml(interaction: PgRendererDto.InteractionDefinition): string {
        let html = "";
        switch (interaction.InteractionTypeId as any) {
            case Enum.InteractionTypeEnum.Choice: {
                const choiceInteraction = interaction.ChoiceInteraction;
                if (choiceInteraction.ResponseDeclaration.AllowPartialScoring === true) {
                    choiceInteraction.ResponseDeclaration.Mapping.MapEntries
                        .filter(x => x && x.MappedValue != 0)
                        .map(x => {
                            const choice = choiceInteraction.SimpleChoices.find(c => c.Identifier == x.MapKey);
                            if (choice) {
                                html += `${choice.Html} (Weight: ${x.MappedValue})<br>`;
                            }
                        });
                } else {
                    choiceInteraction.ResponseDeclaration.CorrectResponse.Values.forEach(x => {
                        const choice = choiceInteraction.SimpleChoices.find(c => c.Identifier == x.DataValue);
                        if (choice) {
                            html += `${choice.Html}<br>`;
                        }
                    });
                }
                break;
            }
            case Enum.InteractionTypeEnum.InlineChoice: {
                const inlineChoiceInteraction = interaction.InlineChoiceInteraction;
                inlineChoiceInteraction.ResponseDeclaration.CorrectResponse.Values.forEach(x => {
                    const choice = inlineChoiceInteraction.InlineChoices.find(c => c.Identifier == x.DataValue);
                    if (choice) {
                        html += `${choice.Html}<br>`;
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.Hotspot: {
                const hotspotInteraction = interaction.HotspotInteraction;
                if (hotspotInteraction.ResponseDeclaration.AllowPartialScoring === true) {
                    hotspotInteraction.ResponseDeclaration.Mapping.MapEntries
                        .filter(x => x && x.MappedValue != 0)
                        .map(x => html += `${x.MapKey} (Weight: ${x.MappedValue})<br>`);
                } else {
                    hotspotInteraction.ResponseDeclaration.CorrectResponse.Values.forEach(x => html += `${x.DataValue}<br>`);
                }
                break;
            }
            case Enum.InteractionTypeEnum.Order: {
                const orderInteraction = interaction.OrderInteraction;
                orderInteraction.SimpleChoices.forEach(choice => {
                    html += `${choice.Html}<br>`;
                });
                break;
            }
            case Enum.InteractionTypeEnum.Associate: {
                let tempHtml: string;
                interaction.AssociateInteraction.ResponseDeclaration.Mapping.MapEntries.forEach((entry, entryIndex) => {
                    tempHtml = "";
                    const keys = entry.MapKey.split(" ");
                    keys.forEach((key, keyIndex) => {
                        const choice = interaction.AssociateInteraction.SimpleAssociableChoices.find(x => x.Identifier == key);
                        if (choice) {
                            tempHtml += `${choice.Html}`;
                            if (keyIndex == 0) {
                                tempHtml += " : ";
                            }
                        }
                    });
                    if (tempHtml.length > 0) {
                        html += tempHtml + "<br>";
                    }
                });
                break;
            }
            case Enum.InteractionTypeEnum.Match: {
                const matchInteraction = interaction.MatchInteraction;
                const groups = matchInteraction.SimpleMatchSets[1].SimpleAssociableChoices;
                const choices = matchInteraction.SimpleMatchSets[0].SimpleAssociableChoices;
                groups.forEach(group => {
                    html += `<strong>${group.Html}</strong><br>`;
                    const groupChoices = matchInteraction.ResponseDeclaration.Mapping.MapEntries
                        .filter(entry => entry.MapKey.indexOf(group.Identifier) >= 0)
                        .forEach(entry => {
                            const choice = choices.find(c => entry.MapKey.indexOf(c.Identifier) >= 0);
                            if (choice) {
                                html += `- ${choice.Html} <br>`;
                            }
                        });
                    html += "<br>";
                });
                break;
            }
            case Enum.InteractionTypeEnum.TextEntry: {
                const textEntryInteraction = interaction.TextEntryInteraction;
                if (textEntryInteraction.ResponseRatingType == Enum.RatingTypeEnum.MachineScored as any) {
                    html += textEntryInteraction.ResponseDeclaration.Mapping.MapEntries
                        .map(entry => `${entry.MapKey}${entry.MappedValue != 1 ? ` (${entry.MappedValue})` : ""}`)
                        .join(', ');
                }
                break;
            }
            default:
                break;
        }
        return html.trim();
    }

}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/services/preview.service.ts